/**
 * Recovery Action Engine — Guided Revenue Leak Response Layer
 *
 * Maps each leak type to a concrete recovery action with traceability.
 * Does NOT duplicate detection logic — consumes revenueLeakSelectors output.
 * Includes deduplication guard and lifecycle status tracking.
 */

import type { LeakType, UrgencyLevel, LeakCategory } from "./revenueLeakSelectors";

/* ═══════ TYPES ═══════ */

export type RecoveryActionType =
  | "create_followup"
  | "create_reschedule"
  | "escalate_manager"
  | "reactivate_followup"
  | "start_outreach"
  | "log_result";

export type RecoveryStatus = "pending" | "in_progress" | "completed" | "ignored";

export interface RecoveryActionDef {
  actionType: RecoveryActionType;
  label: string;
  secondaryLabel?: string;
  targetModule: string;
  targetRoute: string;
  suggestedNextStep: string;
  canAutoCreate: boolean;
  requiresConfirmation: boolean;
  icon: string;
}

export interface RecoveryAction {
  id: string;
  leakType: LeakType;
  recordId: string;
  recordLabel: string;
  revenueAtRisk: number;
  urgency: UrgencyLevel;
  action: RecoveryActionDef;
  secondaryAction?: RecoveryActionDef;
  createdAt: string;
  actor: "system" | "user";
  status: RecoveryStatus;
}

export interface RecoveryActionLog {
  id: string;
  sourceLeakType: LeakType;
  recordId: string;
  actionType: RecoveryActionType | "duplicate_prevented" | "status_transition";
  timestamp: string;
  actor: string;
  details: string;
  fromStatus?: RecoveryStatus;
  toStatus?: RecoveryStatus;
}

/* ═══════ ACTION DEFINITIONS ═══════ */

const ACTION_DEFS: Record<LeakType, { primary: RecoveryActionDef; secondary?: RecoveryActionDef }> = {
  no_sale_no_followup: {
    primary: {
      actionType: "create_followup",
      label: "Create Recovery Follow-Up",
      targetModule: "follow-ups",
      targetRoute: "/follow-ups",
      suggestedNextStep: "Create a follow-up task to re-engage this prospect after the no-sale outcome",
      canAutoCreate: true,
      requiresConfirmation: false,
      icon: "PhoneCallback",
    },
  },
  no_demo_no_reschedule: {
    primary: {
      actionType: "create_reschedule",
      label: "Recover No Demo",
      targetModule: "appointments",
      targetRoute: "/opportunities",
      suggestedNextStep: "Schedule a new appointment to recover this missed demo opportunity",
      canAutoCreate: true,
      requiresConfirmation: false,
      icon: "CalendarPlus",
    },
  },
  high_value_lost: {
    primary: {
      actionType: "escalate_manager",
      label: "Escalate High-Value Deal",
      targetModule: "follow-ups",
      targetRoute: "/follow-ups",
      suggestedNextStep: "Create a manager-priority follow-up to review and recover this high-value opportunity",
      canAutoCreate: true,
      requiresConfirmation: true,
      icon: "ShieldAlert",
    },
    secondary: {
      actionType: "create_followup",
      label: "Create Priority Follow-Up",
      targetModule: "follow-ups",
      targetRoute: "/follow-ups",
      suggestedNextStep: "Assign a prioritized follow-up with the original rep",
      canAutoCreate: true,
      requiresConfirmation: false,
      icon: "PhoneCallback",
    },
  },
  stale_followups: {
    primary: {
      actionType: "reactivate_followup",
      label: "Reactivate Follow-Up",
      targetModule: "follow-ups",
      targetRoute: "/follow-ups",
      suggestedNextStep: "Bump priority and reassign or escalate this overdue follow-up",
      canAutoCreate: true,
      requiresConfirmation: false,
      icon: "RefreshCw",
    },
  },
  uncontacted_leads: {
    primary: {
      actionType: "start_outreach",
      label: "Start Outreach",
      targetModule: "pipeline",
      targetRoute: "/pipeline",
      suggestedNextStep: "Initiate first contact with this uncontacted lead immediately",
      canAutoCreate: true,
      requiresConfirmation: false,
      icon: "Send",
    },
  },
  appointments_without_outcome: {
    primary: {
      actionType: "log_result",
      label: "Log Appointment Result",
      targetModule: "appointment-results",
      targetRoute: "/appointment-results",
      suggestedNextStep: "Record the outcome of this completed appointment to clear the leak",
      canAutoCreate: false,
      requiresConfirmation: true,
      icon: "ClipboardCheck",
    },
  },
};

/* ═══════ CORE ENGINE ═══════ */

export function getRecoveryDef(leakType: LeakType): { primary: RecoveryActionDef; secondary?: RecoveryActionDef } {
  return ACTION_DEFS[leakType];
}

/**
 * Build prioritized recovery actions from leak categories.
 * Sorts by revenue at risk → urgency → count.
 */
export function buildRecoveryActions(categories: LeakCategory[]): RecoveryAction[] {
  const actions: RecoveryAction[] = [];
  const now = new Date().toISOString();

  for (const cat of categories) {
    if (cat.count === 0) continue;
    const defs = ACTION_DEFS[cat.type];

    for (const item of cat.items) {
      const record = item as any;
      const recordId = record.id || record.opportunityId || record.leadId || "unknown";
      const recordLabel =
        record.customerName || record.primaryName ||
        record.opportunity?.primaryName || recordId;
      const itemRevenue =
        record.pricingSnapshot?.bestValuePrice ||
        record.totalEstimate ||
        record.dealValue || 0;

      actions.push({
        id: `recovery-${cat.type}-${recordId}`,
        leakType: cat.type,
        recordId,
        recordLabel,
        revenueAtRisk: itemRevenue,
        urgency: cat.urgency,
        action: defs.primary,
        secondaryAction: defs.secondary,
        createdAt: now,
        actor: "system",
        status: "pending",
      });
    }
  }

  const urgencyOrder: Record<UrgencyLevel, number> = { high: 0, medium: 1, low: 2 };
  actions.sort((a, b) => {
    const revDiff = b.revenueAtRisk - a.revenueAtRisk;
    if (revDiff !== 0) return revDiff;
    return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
  });

  return actions;
}

/* ═══════ DEDUPLICATION GUARD ═══════ */

/**
 * Check if a recovery action has already been executed for a given record + action type.
 */
export function isDuplicateRecovery(
  recordId: string,
  actionType: RecoveryActionType,
  statusMap: Map<string, RecoveryStatus>,
): boolean {
  const key = makeDedupeKey(recordId, actionType);
  const status = statusMap.get(key);
  return status === "in_progress" || status === "completed";
}

export function makeDedupeKey(recordId: string, actionType: RecoveryActionType): string {
  return `${actionType}::${recordId}`;
}

/* ═══════ ACTION LOG ═══════ */

const _actionLog: RecoveryActionLog[] = [];

export function logRecoveryAction(entry: Omit<RecoveryActionLog, "id" | "timestamp">): RecoveryActionLog {
  const log: RecoveryActionLog = {
    ...entry,
    id: `ral-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp: new Date().toISOString(),
  };
  _actionLog.push(log);
  return log;
}

export function getRecoveryActionLog(): RecoveryActionLog[] {
  return [..._actionLog];
}

export function clearRecoveryActionLog(): void {
  _actionLog.length = 0;
}
